'use client';

import { CardCarousel } from "./CardCarousel";

// import { LookbookCard } from "./LookbookCard";
import { Box, Spinner, Stack, Text } from "@chakra-ui/react";
import ProductCard from "./ProductCard";
import GiftBoxCard from "./GiftBoxCard";
import { GiftBox } from "@/types/giftbox";
import { Product } from "@/types/product";
import { Lookbook } from "@/types/lookbook";
import LookbookShowcase from "./LookbookShowcase";
import { useTranslations } from "next-intl";

type ShowcaseType = "product" | "giftbox" | "lookbook";

type Showcase<T = Entity> = {
  id: number;
  key: string;
  title: string;
  link: string;
  type: ShowcaseType;
  entities: T[];
};

type Entity =
  | ({ type: "product" } & Product)
  | ({ type: "giftbox" } & GiftBox)
  | ({ type: "lookbook"; id: string | number } & Lookbook);

interface UniversalShowcaseCarouselProps {
  showcases: Showcase[];
  isLoading?: boolean;
  error?: string;
}

export const UniversalShowcaseCarousel: React.FC<UniversalShowcaseCarouselProps> = ({
  showcases,
  isLoading,
  error,
}) => {
  const t = useTranslations();
  if (isLoading) return <Spinner />;
  if (error) return <Text color="red.500">{error}</Text>;
  if (!showcases.length) return <Box>{t('no_products')}</Box>;

  return (
    <Stack id="universal-showcase-carousel" gap={{ base: "128px", md: "80px" }}>
      {showcases.map(showcase => {
        switch (showcase.type) {
          case "product":
            return (
              <CardCarousel
                key={`${showcase.id}-${showcase.key}`}
                title={showcase.title}
                link={showcase.link}
                items={showcase.entities
                  .filter(entity => !(entity as Product).hidden)
                  .map(entity => (
                    <ProductCard key={(entity as Product).id} product={entity as Product} bg="#ECECEC" />
                  ))}
              />
            );
          case "giftbox":
            return (
              <CardCarousel
                key={showcase.id}
                title={t('gift_boxes')}
                link={showcase.link}
                items={showcase.entities
                  .filter(entity => !(entity as GiftBox).hidden)
                  .map(entity => (
                    <GiftBoxCard key={(entity as Entity).id} giftbox={entity as GiftBox} />
                  ))}
              />
            );
          case "lookbook":
            return <LookbookShowcase key={showcase.id}  items={showcase.entities.filter(entity => !(entity as Lookbook).hidden) as Lookbook[]} />
          default:
            return <Box>Неизвестная сущность</Box>;
        }
      })}
    </Stack>
  );
};

// TODO:
// - Добавить поддержку lookbook
// - Добавить поддержку универсального получения данных с админки (Showcase)

// GET /api/showcases
// Получить список всех подборок (showcase) с их сущностями

// GET /api/showcases/:id
// Получить одну подборку по id

// Пример: /api/showcases?type=product
// Получить подборки только с товарами

// type Showcase = {
//   id: string | number;
//   title: string;           // Название подборки (например, "Популярные товары")
//   link: string;            // Ссылка на полный просмотр подборки
//   type: "product" | "giftbox" | "lookbook"; // Тип сущностей внутри
//   entities: Entity[];      // Массив сущностей (товары, гифтбоксы и т.д.)
// };

// const showcases = [
//   {
//     id: 1,
//     title: "Популярные товары",
//     link: "/catalog/popular",
//     type: "product",
//     entities: [
//       { type: "product", id: "p1", name: "Товар 1", image: "/img1.jpg", price: 1000, /* ...другие поля Product */ },
//       { type: "product", id: "p2", name: "Товар 2", image: "/img2.jpg", price: 2000, /* ... */ },
//     ]
//   },
//   {
//     id: 2,
//     title: "Гифтбоксы",
//     link: "/giftboxes",
//     type: "giftbox",
//     entities: [
//       { type: "giftbox", id: "g1", title: "Подарок 1", image: "/gift1.jpg", items: ["a", "b"], /* ...другие поля GiftBox */ },
//       { type: "giftbox", id: "g2", title: "Подарок 2", image: "/gift2.jpg", items: ["c"], /* ... */ },
//     ]
//   }
// ];